<script setup>
import {
  Battery50Icon,
  CircleStackIcon,
  ChevronRightIcon,
  ClockIcon,
  CpuChipIcon,
  InformationCircleIcon,
} from '@heroicons/vue/24/outline'
import { CheckCircleIcon } from '@heroicons/vue/24/solid'
import { useI18n } from '../../i18n.js'

const { locale, t } = useI18n()

defineProps({ health: { type: Object, required: true } })
const emit = defineEmits(['show-controllers'])

function firmware(value, version) {
  const date = value ? new Date(value) : null
  const suffix = date && !Number.isNaN(date.getTime())
    ? ` (${new Intl.DateTimeFormat(locale.value, { year: 'numeric', month: '2-digit', day: '2-digit' }).format(date)})`
    : ''
  return version ? `${version}${suffix}` : (suffix ? suffix.slice(2, -1) : '—')
}

function decimal(value, digits = 1) {
  return value == null ? '—' : new Intl.NumberFormat(locale.value, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value)
}

function bytes(value) {
  return value == null ? '—' : `${decimal(value / 1_000_000_000)} ${t('pts2UiGigabytes')}`
}

function uptime(seconds) {
  if (seconds == null) return '—'
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  return t('pts2UiUptime', { days, hours, minutes })
}

function batteryState(value) {
  if (value == null) return '—'
  return `${t(value >= 3 ? 'pts2UiBatteryNormal' : 'pts2UiBatteryLow')} (${decimal(value, 2)} ${t('pts2UiVolts')})`
}
</script>

<template>
  <section class="pts2-side-card pts2-health-card" aria-labelledby="pts2-health-title">
    <h2 id="pts2-health-title">{{ t('pts2UiHealth') }}</h2>
    <dl>
      <div><dt><InformationCircleIcon aria-hidden="true" />{{ t('pts2UiFirmware') }}</dt><dd>{{ firmware(health.firmwareDateTime, health.firmwareVersion) }}</dd></div>
      <div><dt><CpuChipIcon aria-hidden="true" />{{ t('pts2UiCpuTemperature') }}</dt><dd>{{ health.cpuTemperature == null ? '—' : `${decimal(health.cpuTemperature, 0)} °C` }}</dd></div>
      <div><dt><Battery50Icon aria-hidden="true" />{{ t('pts2UiBattery') }}</dt><dd class="pts2-battery-value" :class="{ healthy: health.batteryVoltage >= 3 }"><CheckCircleIcon v-if="health.batteryVoltage >= 3" aria-hidden="true" />{{ batteryState(health.batteryVoltage) }}</dd></div>
      <div><dt><CircleStackIcon aria-hidden="true" />{{ t('pts2UiStorage') }}</dt><dd>{{ bytes(health.storage.usedBytes) }} / {{ bytes(health.storage.totalBytes) }}</dd></div>
      <div><dt><ClockIcon aria-hidden="true" />{{ t('pts2UiUptimeLabel') }}</dt><dd>{{ uptime(health.uptimeSeconds) }}</dd></div>
    </dl>
    <button class="pts2-link-action" type="button" @click="emit('show-controllers')">{{ t('pts2UiAllControllers') }} <ChevronRightIcon aria-hidden="true" /></button>
  </section>
</template>
